import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import PaginatedTable from "./paginated-table";
import { useTablePaginationContext } from "./table-pagination-context";

const ApiConnectedPaginatedTable = ({
  APIPetition,
  paginatedElementsHandler,
  manuallyScopedQuery,
  debounce,
  variables,
  defaultOrdering = {},
  onEmpty,
  page,
  rowProps,
  onChangePerPage: onChangePerPageCustom,
  onChangePage,
  tableHeader,
  elements: elementsProp,
  loading: loadingProp,
  error: errorProp,
  total: totalProp,
  perPage: perPageProp,
  noDataMessage = "event.tabs.clips.no.data",
  ...props
}) => {
  const [orderBy, setOrderBy] = useState(defaultOrdering);
  const { tablePaginationSize, handleSetPaginationSize } = useTablePaginationContext();

  const [internalElements, setInternalElements] = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalError, setInternalError] = useState(null);
  const [internalTotal, setInternalTotal] = useState(0);
  const isMountedRef = useRef(true);

  const elements = elementsProp !== undefined ? elementsProp : internalElements;
  const loading = loadingProp !== undefined ? loadingProp : internalLoading;
  const error = errorProp !== undefined ? errorProp : internalError;
  const total = totalProp !== undefined ? totalProp : internalTotal;
  const perPage = perPageProp ?? tablePaginationSize;

  const onChangeOrdering = name => {
    if (Boolean(orderBy) && orderBy.hasOwnProperty(name)) {
      const lastOrdering = orderBy[name];
      const newOrdering = lastOrdering.toUpperCase() === "ASC" ? "DESC" : "ASC";
      setOrderBy({
        ...orderBy,
        [name]: newOrdering,
      });
    }
  };

  const prevParamsRef = useRef();

  useEffect(
    () => {
      isMountedRef.current = true;

      if (elementsProp === undefined && APIPetition) {
        const currentParams = JSON.stringify({ variables, page, perPage, orderBy });

        if (prevParamsRef.current === currentParams) {
          return;
        }

        prevParamsRef.current = currentParams;
        setInternalLoading(true);
        setInternalError(null);

        try {
          const result = APIPetition(
            {
              ...variables,
              page,
              perPage,
              orderBy,
            },
            response => {
              if (!isMountedRef.current) return;

              try {
                const data = response?.data || response;
                const processedElements = paginatedElementsHandler ? paginatedElementsHandler(data) : data;
                const elementsArray = Array.isArray(processedElements)
                  ? processedElements
                  : processedElements?.items || [];
                setInternalElements(elementsArray);
                setInternalTotal(processedElements?.total || elementsArray.length);
                setInternalLoading(false);
              } catch (err) {
                setInternalError(err);
                setInternalLoading(false);
                setInternalElements([]);
                setInternalTotal(0);
              }
            },
          );

          if (result && result.catch) {
            result.catch(err => {
              if (!isMountedRef.current) return;
              setInternalError(err);
              setInternalLoading(false);
              setInternalElements([]);
              setInternalTotal(0);
            });
          }
        } catch (err) {
          setInternalError(err);
          setInternalLoading(false);
          setInternalElements([]);
          setInternalTotal(0);
        }
      }

      return () => {
        isMountedRef.current = false;
      };
    },
    [APIPetition, variables, page, perPage, orderBy, elementsProp, paginatedElementsHandler],
  );

  useEffect(
    () => {
      if (onEmpty && !total) {
        onEmpty();
      }
    },
    [elements, onEmpty, total],
  );

  return (
    <PaginatedTable
      noDataMessage={noDataMessage}
      paginatedElements={{ items: elements, total: total }}
      perPage={perPage}
      onChangePage={onChangePage}
      onChangePerPage={ev => {
        const elementsPerPage = parseInt(ev.target.value, 10);
        onChangePerPageCustom ? onChangePerPageCustom(elementsPerPage) : handleSetPaginationSize(elementsPerPage);
      }}
      rowProps={{ onChangeOrdering, orderBy, ...rowProps }}
      isUrlPagination={false}
      page={page}
      loading={loading}
      error={error}
      {...props}
    />
  );
};

ApiConnectedPaginatedTable.propTypes = {
  /**
   * Número de página a cargar
   */
  page: PropTypes.number,
  /**
   * Clase adicional del componente.
   */
  className: PropTypes.string,
  /**
   * Función que lanza la llamada a la API
   **/
  APIPetition: PropTypes.func.isRequired,
  /**
   * Función que obtiene los elementos a representar de los datos devueltos por el servidor
   **/
  paginatedElementsHandler: PropTypes.func.isRequired,
  /**
   * Componente que renderiza una fila de la tabla a partir de un elemento
   **/
  rowComponent: PropTypes.elementType.isRequired,
  /**
   * Props adicionales para el componente de fila
   **/
  rowProps: PropTypes.object,
  /**
   * Contenido de la cabecera de la tabla
   **/
  headerComponent: PropTypes.elementType,
  /**
   * Variables de la query (sin customerId, que se inyecta)
   **/
  variables: PropTypes.object,
  /**
   * Conjunto de variables de ordenación activas (par clave, valor)
   **/
  defaultOrdering: PropTypes.object,
  /**
   * Muestra o no los controles de paginación
   **/
  showPagination: PropTypes.bool,
  /**
   * Indica si la query se debouncea.
   **/
  debounce: PropTypes.bool,
  /**
   * Footer opcional de tabla
   **/
  footer: PropTypes.node,
  /**
   * Indica si la query de superadmin se ejecuta en un contexto de selección manual del customer
   **/
  manuallyScopedQuery: PropTypes.bool,
  /**
   * Callback que se llama si la tabla está vacía
   **/
  onEmpty: PropTypes.func,
  /**
   * Callback que se llama si la tabla está vacía
   **/
  onChangePage: PropTypes.func,
  /**
   * Callback que se llama cuando se cambia el número de elementos por página
   **/
  onChangePerPage: PropTypes.func,
  /**
   * Componente que se renderiza encima de la tabla
   **/
  tableHeader: PropTypes.element,
};

export { ApiConnectedPaginatedTable };
export default ApiConnectedPaginatedTable;
